import { Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import InsightsIcon from '@mui/icons-material/Insights';
import SensorsIcon from '@mui/icons-material/Sensors';
import StopCircleIcon from '@mui/icons-material/StopCircle';

/**
 * Same as IDash4You.Partner.UI src/screening/components/FilterBar.tsx (Canada) minus the Currency and
 * Category filters (India has neither). `market` = { open, label } from the API's market check.
 * Filters apply when Analyze (or Live Screening) is pressed - not on change - and lock while live.
 */
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  70% { box-shadow: 0 0 0 7px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
`;

export default function FilterBar(p) {
  return (
    <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: 1, maxWidth: { md: 640 }, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          <Select label="Algorithm" value={p.algoKey} options={p.algorithms} onChange={p.onAlgoKey} disabled={p.live} />
          <Select label="Symbols" value={p.universe} options={p.universes} onChange={p.onUniverse} disabled={p.live} />
        </Box>
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
          {p.live ? (
            <Button
              variant="contained"
              color="error"
              onClick={p.onStopLive}
              disabled={p.liveBusy}
              startIcon={p.liveBusy ? <CircularProgress size={16} color="inherit" /> : <StopCircleIcon />}
              sx={{ whiteSpace: 'nowrap', py: 1 }}
            >
              Stop Screening
            </Button>
          ) : (
            <Tooltip title={p.market.open ? 'Refresh live quotes and trend scores every minute' : `${p.market.label} - Live Screening runs during trading hours`}>
              <Box component="span" sx={{ display: 'flex' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  disabled={!p.market.open || p.liveBusy || !p.algoKey}
                  onClick={p.onLive}
                  startIcon={p.liveBusy ? <CircularProgress size={16} color="inherit" /> : <SensorsIcon />}
                  sx={{ whiteSpace: 'nowrap', py: 1 }}
                >
                  {p.liveBusy ? 'Starting…' : 'Live Screening'}
                </Button>
              </Box>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={p.onAnalyze}
            disabled={p.analyzing || !p.algoKey || p.live || p.liveBusy}
            startIcon={p.analyzing ? <CircularProgress size={16} color="inherit" /> : <InsightsIcon />}
            sx={{ whiteSpace: 'nowrap', py: 1, px: 3 }}
          >
            {p.analyzing ? 'Analyzing…' : 'Analyze'}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
        <Box
          sx={{
            width: 8, height: 8, borderRadius: '50%',
            bgcolor: p.market.open ? '#22c55e' : (t) => alpha(t.palette.text.secondary, 0.5),
            animation: p.market.open ? `${pulse} 2s infinite` : 'none',
          }}
        />
        <Typography variant="caption" color="text.secondary">{p.market.label}</Typography>
      </Stack>
    </Paper>
  );
}

function Select({ label, value, options, onChange, disabled = false }) {
  return (
    <TextField select size="small" fullWidth label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}
